import { css } from "@emotion/css";
import { ActionButton, ActionButtonType, DialogContent, FileUpload, CheckboxGroupNew, CheckboxNew, TextField, MultiSelect, Textarea } from "@octopusdeploy/design-system-components";
import { space } from "@octopusdeploy/design-system-tokens";
import { logger } from "@octopusdeploy/logging";
import { useInlineStatusQuery } from "@octopusdeploy/octopus-react-client";
import { Permission, OctopusError } from "@octopusdeploy/octopus-server-client";
import type { UserResource, CreateSupportRequestCommand, SupportFileMetadata } from "@octopusdeploy/octopus-server-client";
import { useState, useCallback, useEffect, useMemo } from "react";
import { repository, session } from "~/clientInstance";
import ErrorPanel from "~/components/ErrorPanel/ErrorPanel";
import { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import { userDisplayName } from "../Chips/UserChip";
import Dialog from "../Dialog/Dialog";
import { SupportTicketSuccessDialog } from "./SupportTicketSuccessDialog";
const ALLOWED_FILE_EXTENSIONS = ["txt", "log", "json", "xml", "config", "yml", "yaml", "properties", "har", "zip", "tar", "gz", "png", "jpg", "jpeg", "pdf", "csv", "mp4", "webm", "mov"];
const MAX_FILE_SIZE = 1024 * 1024 * 1024; // 1 GB
export interface SupportTicketDialogProps {
    open: boolean;
    onClose: () => void;
}
export interface SupportTicketData {
    name: string;
    email: string;
    includeOtherUsers?: string[];
    subject: string;
    description: string;
    attachments: File[];
    includeSystemReport: boolean;
}
const styles = {
    formRow: css({
        display: "flex",
        gap: space[16],
    }),
    formField: css({
        flex: 1,
    }),
    section: css({
        display: "flex",
        flexDirection: "column",
        gap: space[16],
    }),
    dialogContent: css({
        display: "flex",
        flexDirection: "column",
        gap: space[16],
    }),
    technicalSection: css({
        paddingTop: space[16],
        gap: space[8],
    }),
};
function isUserResourceArray(users: unknown): users is UserResource[] {
    return Array.isArray(users);
}
export function SupportTicketDialog({ open, onClose }: SupportTicketDialogProps) {
    const [formData, setFormData] = useState<SupportTicketData>({
        name: "",
        email: "",
        includeOtherUsers: [],
        subject: "",
        description: "",
        attachments: [],
        includeSystemReport: true,
    });
    const [submitting, setSubmitting] = useState(false);
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const [systemReportWarning, setSystemReportWarning] = useState<string | null>(null);
    const { result: usersResult = [] } = useInlineStatusQuery(async (repository): Promise<UserResource[]> => (isAllowed({ permission: Permission.UserView }) ? repository.Users.all() : Promise.resolve([])), [], "Load users for support ticket");
    const users = useMemo(() => {
        return usersResult && isUserResourceArray(usersResult) ? usersResult : [];
    }, [usersResult]);
    const uploadSupportFiles = useCallback(async (files: File[]): Promise<SupportFileMetadata[]> => {
        if (!files || files.length === 0) {
            return [];
        }
        const responses = await Promise.all(files.map((file) => repository.Support.uploadSupportFile(file)));
        return responses.map((response) => response.SupportFileMetadata);
    }, []);
    const submitSupportRequest = useCallback(async () => {
        if (submitting)
            return;
        setSubmitting(true);
        try {
            // Convert user IDs to email addresses
            const ccRecipients: string[] = formData.includeOtherUsers?.map((userId) => users.find((u) => u.Id === userId)?.EmailAddress).filter((email): email is string => Boolean(email)) || [];
            // Upload files first if any exist
            const uploadedFileMetadata = await uploadSupportFiles(formData.attachments);
            // Create support request with uploaded file metadata
            const command: CreateSupportRequestCommand = {
                Subject: formData.subject,
                Description: formData.description,
                UserName: formData.name,
                UserEmail: formData.email,
                IncludeSystemReport: formData.includeSystemReport,
                CcRecipients: ccRecipients,
                UploadedFileMetadata: uploadedFileMetadata.length > 0 ? uploadedFileMetadata : undefined,
            };
            const response = await repository.Support.createSupportRequest(command);
            // Capture any system report warning
            setSystemReportWarning(response.SystemReportWarning || null);
            onClose();
            setShowSuccessDialog(true);
        }
        catch (error) {
            logger.error(error, "Failed to submit support request");
            setError(error);
        }
        finally {
            setSubmitting(false);
        }
    }, [formData, users, onClose, submitting, uploadSupportFiles]);
    // Reset form and populate user data when dialog opens
    useEffect(() => {
        if (open) {
            // Reset success dialog state and clear any errors
            setShowSuccessDialog(false);
            setError(null);
            setSystemReportWarning(null);
            // Populate name and email from current user
            if (session.currentUser) {
                setFormData((prev) => ({
                    ...prev,
                    name: session.currentUser?.DisplayName || "",
                    email: session.currentUser?.EmailAddress || "",
                }));
            }
        }
        else {
            // Reset form when dialog closes
            setFormData({
                name: "",
                email: "",
                includeOtherUsers: [],
                subject: "",
                description: "",
                attachments: [],
                includeSystemReport: true,
            });
        }
    }, [open]);
    const handleInputChange = useCallback((field: keyof SupportTicketData) => (value: string | boolean | string[]) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));
    }, []);
    const handleSubmit = useCallback(async () => {
        await submitSupportRequest();
    }, [submitSupportRequest]);
    const handleFilesChanged = useCallback((files: File[]) => {
        setFormData((prev) => ({
            ...prev,
            attachments: files,
        }));
    }, []);
    const handleSuccessDialogClose = useCallback(() => {
        setShowSuccessDialog(false);
    }, []);
    const actions = (<>
            <ActionButton label="Close" onClick={onClose} disabled={submitting}/>
            <ActionButton type={ActionButtonType.Save} label={submitting ? "Submitting..." : "Submit Ticket"} onClick={handleSubmit} disabled={submitting || !formData.subject || !formData.description || !formData.name || !formData.email}/>
        </>);
    return (<>
            <Dialog open={open} title="Lodge support ticket" dialogWidth="600px" actions={actions}>
                <DialogContent className={styles.dialogContent}>
                    {error && (<ErrorPanel message={OctopusError.isOctopusError(error) ? error.ErrorMessage : error.message || "Failed to submit support ticket. Please try again or contact support directly."} errors={OctopusError.isOctopusError(error) ? error.Errors : undefined} scrollToPanel={false}/>)}

                    <div>
                        Contact support using the form below. We'll get back to you shortly. You can also visit our{" "}
                        <a href="https://octopus.com/support" target="_blank" rel="noopener noreferrer">
                            support page
                        </a>
                        .
                    </div>

                    {/* Name and Email Row */}
                    <div className={styles.formRow}>
                        <div className={styles.formField}>
                            <TextField label="Name" value={formData.name} onChange={handleInputChange("name")}/>
                        </div>
                        <div className={styles.formField}>
                            <TextField label="Email" value={formData.email} onChange={handleInputChange("email")} type="email"/>
                        </div>
                    </div>

                    {/* Include other users */}
                    <MultiSelect label="Include other users" value={formData.includeOtherUsers || []} onChange={handleInputChange("includeOtherUsers")} items={users.filter((user) => user.EmailAddress && user.EmailAddress !== session.currentUser?.EmailAddress)} getOption={(user) => ({ label: userDisplayName(user), description: user.EmailAddress, value: user.Id })} allowFilter={true} hasOptionalMarker/>

                    {/* Subject */}
                    <TextField label="Subject" value={formData.subject} onChange={handleInputChange("subject")}/>

                    {/* Description */}
                    <Textarea label="Describe the problem" name="description" value={formData.description} onChange={handleInputChange("description")}/>

                    {/* File upload section */}
                    <div className={styles.section}>
                        <FileUpload label="Upload supporting documents" description={`Supported formats: ${ALLOWED_FILE_EXTENSIONS.join(", ")}`} onChange={handleFilesChanged} filesLimit={10} acceptedFileTypes={ALLOWED_FILE_EXTENSIONS.map((ext) => `.${ext}`)} maxFileSizeBytes={MAX_FILE_SIZE} hasOptionalMarker/>
                    </div>

                    {/* Technical issues section */}
                    <div className={styles.technicalSection}>
                        <CheckboxGroupNew label="For technical issues" value={formData.includeSystemReport ? ["includeSystemReport"] : []} onChange={(values) => handleInputChange("includeSystemReport")(values.includes("includeSystemReport"))} hasOptionalMarker>
                            <CheckboxNew value="includeSystemReport" label="Send System Diagnostics Report"/>
                        </CheckboxGroupNew>
                    </div>

                    <div>Please note that the support team may need temporary read-only access to your instance to understand and diagnose the problem.</div>
                </DialogContent>
            </Dialog>

            <SupportTicketSuccessDialog open={showSuccessDialog} onClose={handleSuccessDialogClose} systemReportWarning={systemReportWarning}/>
        </>);
}
